import { Button, HStack, useDisclosure } from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import InputTypeDataLossWarningModal, {
  needsInputTypeDataLossWarning,
} from 'components/Modals/InputTypeDataLossWarningModal';
import { useAuthContext } from 'context/authContext';
import { getSharedModelUsers } from 'redux/HeaderSlice';
import { getModelTime, setTabShimmer } from 'redux/IndicatorPageSlice';
import { UpdatePlangPageIndicatorTableData, updateSetDriver } from 'redux/PlanPageSlice';
import { getPlannerModel } from 'redux/PlannerModeSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

interface Properties {
  inputDimensionsData?: { id: string }[];
  inputType: string;
  indicatorId: number;
  blockId: number;
  refetchDrivers: () => void;
  refetchRequest: () => void;
  inputDataValues: Record<string, string>[];
  timeDimension: number;
  timeRange: string[];
  timeRangeYears: string[];
}

const InputTypeGroupedButton = ({ inputType, indicatorId, refetchRequest }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('');
  const {
    isOpen: isDataLossWarningOpen,
    onOpen: onDataLossWarningOpen,
    onClose: onDataLossWarningClose,
  } = useDisclosure();
  const [pendingInputType, setPendingInputType] = useState<string | null>(null);
  const modelTime = useSelector(getModelTime);
  const timeGranularity = modelTime?.properties?.time_granularity?.toLowerCase();
  const baseScenario = useSelector(getBaseScenario);
  const [isAPiLoading, setApiLoading] = useState(false);
  const plannerModel = useSelector(getPlannerModel);
  const tabchange = () => {
    if (inputType !== activeTab) {
      setActiveTab(inputType);
    }
  };

  useEffect(() => {
    setActiveTab(inputType);
  }, [inputType]);
  useEffect(() => {
    tabchange();
  }, [activeTab]);

  const { user } = useAuthContext();
  const shareModelUsers = useSelector(getSharedModelUsers);

  const currentUserPermissions = shareModelUsers.find((sharedUser: any) => {
    return user && sharedUser?.user_id === user?.id;
  });

  const disableChange = isAPiLoading || currentUserPermissions?.role === 'view';

  const performInputTypeChange = useCallback(
    async (newType: string) => {
      if (activeTab === newType) return;
      setApiLoading(true);
      const payloadNew: any = {
        indicator_id: indicatorId,
        request_body: {
          type: newType,
          scenario_id: baseScenario?.id,
          bulk_input: true,
          format_excel_data: true,
        },
      };
      setActiveTab(newType);
      dispatch(setTabShimmer(true));
      await dispatch(UpdatePlangPageIndicatorTableData(payloadNew));
      await refetchRequest();
      dispatch(updateSetDriver(newType));
      setTimeout(() => {
        setApiLoading(false);
      }, 600);
    },
    [activeTab, indicatorId, baseScenario?.id, dispatch, refetchRequest],
  );

  const performInputTypeChangeReference = useRef(performInputTypeChange);
  performInputTypeChangeReference.current = performInputTypeChange;

  const debouncedInputTypeChange = useMemo(
    () =>
      debounce((newType: string) => {
        performInputTypeChangeReference.current(newType).catch(() => {});
      }, 1000),
    [],
  );

  useEffect(
    () => () => {
      debouncedInputTypeChange.cancel();
    },
    [debouncedInputTypeChange],
  );

  const requestInputTypeChange = (newType: string) => {
    if (activeTab === newType || disableChange) return;
    if (needsInputTypeDataLossWarning(activeTab, newType)) {
      setPendingInputType(newType);
      onDataLossWarningOpen();
      return;
    }
    debouncedInputTypeChange(newType);
  };

  const handleConfirmDataLossChange = () => {
    if (pendingInputType != null) {
      performInputTypeChange(pendingInputType).catch(() => {});
    }
    setPendingInputType(null);
    onDataLossWarningClose();
  };

  const handleCancelDataLossChange = () => {
    setPendingInputType(null);
    onDataLossWarningClose();
  };

  useEffect(() => {
    if (
      plannerModel?.time_properties?.time_granularity?.toLowerCase() === 'y' &&
      inputType === 'raw'
    ) {
      debouncedInputTypeChange('constant_by_year');
    } else {
      setActiveTab(inputType);
    }
  }, [inputType, plannerModel?.time_properties?.time_granularity, debouncedInputTypeChange]);
  return (
    <>
      <HStack
        spacing={0}
        align='center'
        w='full'
        minH={10}
        flexShrink={0}
        mb={'0'}
        borderRadius='20px'
        borderWidth={1}
        borderColor='transparent'
        bg={'#8885DD'}
      >
        <Button
          isDisabled={disableChange}
          height={10}
          variant='tab'
          size='tab'
          fontSize={{ base: '9px', md: '12px' }}
          className={inputType === 'constant' ? '#4BB6B9' : 'transparent'}
          onClick={() => requestInputTypeChange('constant')}
          bgColor={activeTab === 'constant' ? '#4BB6B9' : 'transparent'}
          borderRadius={activeTab === 'constant' ? '20px' : '2px'}
          borderColor='transparent'
        >
          Constant
        </Button>
        <>
          <Button
            height={10}
            variant='tab'
            size='tab'
            fontSize={{ base: '9px', md: '12px' }}
            bgColor={activeTab === 'constant_by_year' ? '#4BB6B9' : 'transparent'}
            borderRightWidth={1}
            borderLeftWidth={1}
            onClick={() => {
              requestInputTypeChange('constant_by_year');
            }}
            borderRadius={activeTab === 'constant_by_year' ? '20px' : '2px'}
            borderColor='transparent'
            isDisabled={disableChange}
          >
            Yearly
          </Button>
          {timeGranularity && timeGranularity === 'm' && (
            <Button
              height={10}
              variant='tab'
              size='tab'
              fontSize={{ base: '9px', md: '12px' }}
              className={activeTab === 'raw' ? '#4BB6B9' : 'transparent'}
              onClick={() => {
                requestInputTypeChange('raw');
              }}
              bgColor={activeTab === 'raw' ? '#4BB6B9' : 'transparent'}
              borderRadius={activeTab === 'raw' ? '20px' : '2px'}
              borderColor='transparent'
              isDisabled={disableChange}
            >
              Monthly
            </Button>
          )}
        </>
        <Button
          fontSize={{ base: '9px', md: '12px' }}
          height={10}
          variant='tab'
          size='tab'
          borderColor='transparent'
          bgColor={activeTab === 'growth' ? '#4BB6B9' : 'transparent'}
          onClick={() => {
            requestInputTypeChange('growth');
          }}
          borderRadius={activeTab === 'growth' ? '20px' : '2px'}
          isDisabled={disableChange}
        >
          Growth rate
        </Button>
      </HStack>
      <InputTypeDataLossWarningModal
        isOpen={isDataLossWarningOpen}
        onCancel={handleCancelDataLossChange}
        onConfirm={handleConfirmDataLossChange}
      />
    </>
  );
};

export default InputTypeGroupedButton;
